import { useMemo } from 'react';
import { Box } from '@mui/material';
import { DEFAULT_COLOR } from 'src/config';
import { Svg } from 'src/components/Svg/Svg';
import { useColor } from 'src/hooks/useColor';
import { useIsoProjection } from 'src/hooks/useIsoProjection';
import { useScene } from 'src/hooks/useScene';
import { getConnectorArrows } from '@/utils/connectorDirectionIcon';
import { ModernArrow } from './ConnectorArrow';

interface Props {
  connectors: ReturnType<typeof useScene>['connectors'];
}

interface ConnectorArrowPairProps {
  connector: Props['connectors'][number];
}

const ConnectorArrowPair = ({ connector }: ConnectorArrowPairProps) => {
  const color = useColor(connector.color);
  const colorValue =
    typeof color === 'string' ? color : color?.value || DEFAULT_COLOR.value;
  const { css, pxSize } = useIsoProjection({
    ...connector.path.rectangle
  });

  const arrows = useMemo(() => {
    return getConnectorArrows(
      connector.path.tiles,
      connector.arrowType || 'SINGLE',
      connector.arrowDirection || 'forward'
    );
  }, [connector.path.tiles, connector.arrowType, connector.arrowDirection]);

  if (!arrows.start && !arrows.end) {
    return null;
  }

  return (
    <Box style={{ ...css, pointerEvents: 'none' }}>
      <Svg
        style={{
          transform: 'scale(-1, 1)',
          pointerEvents: 'none',
          // Arrow geometry intentionally extends beyond the connector's tight
          // SVG bounds near endpoints. Without visible overflow, it looks as
          // though the endpoint icon is covering the arrowhead.
          overflow: 'visible'
        }}
        viewboxSize={pxSize}
        aria-hidden="true"
      >
        {arrows.start && (
          <ModernArrow
            x={arrows.start.x}
            y={arrows.start.y}
            rotation={arrows.start.rotation}
            color={colorValue}
            size={1.15}
          />
        )}
        {arrows.end && (
          <ModernArrow
            x={arrows.end.x}
            y={arrows.end.y}
            rotation={arrows.end.rotation}
            color={colorValue}
            size={1.15}
          />
        )}
      </Svg>
    </Box>
  );
};

export const ConnectorArrows = ({ connectors }: Props) => {
  return (
    <>
      {[...connectors].reverse().map((connector) => (
        <ConnectorArrowPair key={connector.id} connector={connector} />
      ))}
    </>
  );
};
